import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { repository } from '../data'
import { serialFromPayload } from './serialFromPayload'
import { useScanner } from './useScanner'

/**
 * Stick, scan, stick, scan (§9.3) — the loop itself, with no page around it.
 *
 * Twenty actuators in about three minutes, so the design is entirely about not
 * interrupting the loop. The type was chosen **once**, before this mounted, and
 * every scan after it is silent unless something is wrong. No confirmation, no
 * dialog, no navigation — just the count going up, which is the only feedback a
 * person with a part in each hand can actually use.
 *
 * It is a component rather than a page because it has two homes: the `/intake`
 * page a blank label lands on, and the Stock tab's Receive dialog, where the
 * shelf is still behind it. Somebody who tapped a part to receive more of it
 * has not asked to leave the shelf, and taking the list away to show a camera
 * is the app deciding otherwise on their behalf.
 *
 * The camera is released by `useScanner`'s own unmount cleanup, so closing the
 * dialog or picking a different type turns it off without anything here saying
 * so.
 *
 * **And it starts itself.** Getting here took a deliberate tap on Receive, on a
 * card the person picked off the shelf list; there is nothing else this screen
 * does. Asking them to then tap "Start scanning" is the app requiring the
 * intent twice, and it read as the feature being broken — a viewfinder-shaped
 * black box saying "Camera off" looks identical to a camera that has failed.
 * The manual button stays for coming back after a deliberate Stop.
 */
export function IntakeLoop({ typeId }: { typeId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const [done, setDone] = useState<string[]>([])
  const [problem, setProblem] = useState<string | null>(null)

  const take = useMutation({
    mutationFn: (serial: string) => repository.intake(serial, typeId, null),
    onSuccess: (result) => {
      setProblem(null)
      setDone((d) => [result.serial, ...d])
      // `stock`, not `parts`: the endpoint is /api/parts but the query is keyed
      // `stock`. With the loop inside the Receive dialog this is visible while
      // it happens — the card behind counts up.
      void queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
    // The server's sentence is the useful one — "that one is already taken in"
    // tells the person to move on, where "request failed" stops the loop.
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const onScan = useCallback(
    (raw: string) => {
      const serial = serialFromPayload(raw)
      if (!serial) {
        setProblem('That is not a Kronos label.')
        return
      }
      if (done.includes(serial)) return // already counted in this session
      take.mutate(serial)
    },
    [done, take],
  )

  const scanner = useScanner(onScan)

  // Mount only. `start` is stable and re-running this would fight a deliberate
  // Stop, which is the one reason somebody turns the camera off mid-delivery.
  const { start, supported } = scanner
  useEffect(() => {
    if (supported) start()
  }, [supported, start])

  return (
    <div className="stack">
      {/* The count is the whole interface. */}
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44, fontWeight: 700, lineHeight: 1.1 }}>{done.length}</div>
        <div className="muted">taken in this session</div>
      </div>

      <div className="scanner">
        <video ref={scanner.videoRef} playsInline muted />
        {scanner.running ? <div className="reticle" /> : null}
        <p className="scan-hint">
          {scanner.running
            ? 'Scan the next label'
            : scanner.starting
              ? 'Starting the camera…'
              : scanner.unavailable
                ? 'Camera unavailable'
                : 'Camera off'}
        </p>
      </div>

      <div className="row">
        {scanner.running ? (
          <button onClick={scanner.stop}>Stop</button>
        ) : (
          <button
            className="primary"
            onClick={scanner.start}
            disabled={!scanner.supported || scanner.starting}
          >
            {scanner.starting ? 'Starting…' : 'Start scanning'}
          </button>
        )}
        <TypedSerial onSubmit={onScan} />
      </div>

      {/* The dialog has no typed-serial hint of its own above, so an
          unopenable camera has to explain itself here or the loop just sits
          there looking broken. */}
      {scanner.unavailable ? <p className="muted">{scanner.unavailable}</p> : null}
      {scanner.error ? <p className="badge critical">{scanner.error}</p> : null}
      {problem ? <p className="badge critical">{problem}</p> : null}

      {done.length > 0 ? (
        <div className="card stack">
          <span className="muted">Most recent first</span>
          {done.slice(0, 12).map((s) => (
            <span key={s} className="serial">{s}</span>
          ))}
          {done.length > 12 ? <span className="muted">and {done.length - 12} more</span> : null}
        </div>
      ) : null}
    </div>
  )
}

/** The typed fallback, and what the USB bench scanner types into (§9.4). */
function TypedSerial({ onSubmit }: { onSubmit: (raw: string) => void }): JSX.Element {
  const [value, setValue] = useState('')
  return (
    <form
      className="row"
      style={{ flex: 1 }}
      onSubmit={(e) => {
        e.preventDefault()
        if (!value.trim()) return
        onSubmit(value)
        setValue('') // cleared immediately, so the scanner can fire again
      }}
    >
      <input
        className="serial"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="or type"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        maxLength={7}
        aria-label="Serial"
        style={{
          flex: 1,
          minHeight: 'var(--tap)',
          padding: '0 12px',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius)',
          background: 'var(--bg-sunken)',
        }}
      />
    </form>
  )
}
